import React, { useState } from "react";
import { Link } from "react-router-dom";
import { categories, bunnyEmbed, showreelIds } from "../data/categoriesData";

const adFilmsCategory = categories.find((c) => c.id === "ad-films");

const MOMENTA_WORDMARK =
  "https://customer-assets.emergentagent.com/job_momenta-works/artifacts/rx6m3rj4_MOMENTA%20STUDIOS%20WHITE.png";

// A single Bunny film card — iframe with autoplay=false (Bunny shows its
// own thumbnail + play button). Click play → inline playback. Bunny's
// player has built-in fullscreen, picture-in-picture, captions etc.
const FilmCard = ({ project, idx, size = "regular" }) => {
  const total = project.videos?.length || 0;
  const [filmIdx, setFilmIdx] = useState(0);
  const id = project.videos[filmIdx];
  const prev = () => setFilmIdx((i) => (i - 1 + total) % total);
  const next = () => setFilmIdx((i) => (i + 1) % total);

  return (
    <div data-testid={`adfilms-card-${project.id}`} className="group">
      <div className="flex items-end justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h2
            className={`font-serif italic text-white tracking-tighter leading-none ${
              size === "hero"
                ? "text-5xl sm:text-7xl lg:text-8xl"
                : "text-3xl sm:text-4xl lg:text-5xl"
            }`}
          >
            {project.name}
          </h2>
        </div>
        {total > 1 && (
          <div className="text-white/70 font-mono text-[11px] tracking-[0.3em] uppercase shrink-0">
            {String(filmIdx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>
        )}
      </div>

      <div className="relative aspect-video w-full bg-black overflow-hidden">
        <iframe
          key={`${id}-${filmIdx}`}
          src={bunnyEmbed(id, { autoplay: false, muted: true })}
          title={`${project.name} film ${filmIdx + 1}`}
          loading="lazy"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />

        {total > 1 && (
          <>
            <button
              onClick={prev}
              data-testid={`adfilms-card-prev-${project.id}`}
              aria-label="Previous film"
              className="absolute top-1/2 -translate-y-1/2 left-3 sm:left-5 w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-black/55 hover:bg-black/85 text-white border border-white/40 hover:border-white backdrop-blur transition-all z-10"
            >
              <span className="text-2xl leading-none">←</span>
            </button>
            <button
              onClick={next}
              data-testid={`adfilms-card-next-${project.id}`}
              aria-label="Next film"
              className="absolute top-1/2 -translate-y-1/2 right-3 sm:right-5 w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-black/55 hover:bg-black/85 text-white border border-white/40 hover:border-white backdrop-blur transition-all z-10"
            >
              <span className="text-2xl leading-none">→</span>
            </button>
          </>
        )}
      </div>

      {total > 1 && (
        <div className="mt-4 flex items-center justify-center gap-1.5">
          {project.videos.map((_, i) => (
            <button
              key={i}
              onClick={() => setFilmIdx(i)}
              aria-label={`Film ${i + 1}`}
              className={`h-[2px] transition-all ${i === filmIdx ? "bg-white w-8" : "bg-white/25 w-4 hover:bg-white/50"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Auto-cycling Bunny iframe banner. Loads ONE iframe with autoplay=true
// + muted, swaps src on a timer. Provides the cinematic intro to the page.
const ShowreelBanner = () => {
  const [idx, setIdx] = useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % showreelIds.length), 9000);
    return () => clearInterval(t);
  }, []);
  return (
    <div
      aria-hidden
      data-testid="adfilms-showreel"
      className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none"
    >
      <iframe
        key={showreelIds[idx]}
        src={bunnyEmbed(showreelIds[idx], { autoplay: true, muted: true })}
        title=""
        allow="autoplay; encrypted-media"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{ width: "max(177.78vh, 100%)", height: "max(56.25vw, 100%)", border: 0 }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black" />
      <div className="absolute inset-0 bg-black/20" />
    </div>
  );
};

export default function AdFilmsPage() {
  const category = adFilmsCategory;
  if (!category) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Link to="/" className="link-underline eyebrow">← Back to home</Link>
      </div>
    );
  }

  // First project = hero feature, rest in a 2-col editorial grid.
  const [hero, ...rest] = category.projects;

  return (
    <div data-testid="adfilms-page" className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="adfilms-logo">
            <img src={MOMENTA_WORDMARK} alt="Momenta Studios" className="h-8 sm:h-9 w-auto object-contain" draggable={false} />
          </Link>
          <Link to="/" data-testid="adfilms-back" className="text-[11px] tracking-[0.3em] uppercase text-white/70 hover:text-white link-underline">
            ← Back
          </Link>
        </div>
      </header>

      <section className="relative min-h-[55vh] sm:min-h-[80vh] flex items-end px-5 sm:px-10 lg:px-14 py-14 sm:py-28 border-b border-white/10 overflow-hidden">
        <ShowreelBanner />
        <div className="relative z-10 max-w-[1600px] mx-auto w-full">
          <h1 className="font-serif text-white text-6xl sm:text-8xl lg:text-[11rem] font-light tracking-tighter leading-[0.85] drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]">
            Ad <em className="italic font-medium">Films</em><span className="font-light">.</span>
          </h1>
          <p className="hidden md:block mt-6 sm:mt-8 max-w-2xl text-white text-base sm:text-lg font-light leading-relaxed drop-shadow-[0_2px_18px_rgba(0,0,0,0.7)]">
            Brand films + commercials shot by a full Momenta crew. Direct play — no buffering.
          </p>
        </div>
      </section>

      {/* Hero feature card */}
      {hero && hero.videos.length > 0 && (
        <section className="border-b border-white/10 px-5 sm:px-10 lg:px-14 py-14 sm:py-20">
          <div className="max-w-[1600px] mx-auto">
            <FilmCard project={hero} idx={0} size="hero" />
          </div>
        </section>
      )}

      {/* Editorial grid for the rest */}
      <section className="max-w-[1600px] mx-auto px-5 sm:px-10 lg:px-14 py-14 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-14 lg:gap-16">
          {rest.map((p, pi) => (
            p.videos.length > 0 ? (
              <FilmCard key={p.id} project={p} idx={pi + 1} />
            ) : (
              <div key={p.id} className="border border-dashed border-white/15 p-8 sm:p-14 text-white/50 text-sm text-center">
                <div className="font-serif italic text-2xl text-white/70 mb-2">{p.name}</div>
                Film to be uploaded shortly.
              </div>
            )
          ))}
        </div>

        <div className="pt-16 sm:pt-24 text-center">
          <Link
            to="/#contact"
            data-testid="adfilms-cta-brief"
            className="inline-flex items-center gap-4 bg-white text-black hover:bg-white/85 px-6 sm:px-8 py-4 text-[11px] tracking-[0.3em] uppercase"
          >
            Send a Brief →
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 sm:py-10 px-5 sm:px-10">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between flex-wrap gap-3 text-white/40 text-[10px] tracking-[0.3em] uppercase font-mono">
          <div>© {new Date().getFullYear()} Momenta Studios</div>
          <Link to="/" className="link-underline hover:text-white">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}
